import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAnimals } from "../../../hooks/useAnimals";
import { useCustomers } from "../../../hooks/useCustomers";
import { useUsers } from "../../../hooks/useUsers";
import {
  getDeliveryAssignments,
  rehydrateDeliveryAssignments,
  subscribeDeliveryAssignments,
  updateCarrierLiveLocation,
  upsertDeliveryAssignment,
  type DeliveryAssignment,
} from "../../../lib/deliveryStore";
import {
  clearSessionUser,
  getDefaultPathForRole,
  mustForcePasswordChange,
  readSessionUser,
  restoreSessionFromApi,
} from "../../../lib/authSession";
import type { AnimalItem } from "../../../mocks/animals";
import type { CustomerItem } from "../../../mocks/customers";
import { sendSmsByTemplate } from "../../../lib/smsService";
import {
  getDisplayKupeNo,
  getDisplaySiparisNo,
} from "../../../lib/animalLabels";

interface CarrierRow {
  id: string;
  animalId: number;
  shareId: number;
  kupeNo: string;
  siparisNo: string;
  tur: AnimalItem["tur"];
  customerId: number | undefined;
  customerName: string;
  customerPhone: string;
  address: string;
  shareNo: string;
  hisseSayisi: number;
  status: DeliveryAssignment["status"];
  liveLocationUrl?: string;
  startedAt?: string;
  deliveredAt?: string;
  note?: string;
  delivery: DeliveryAssignment;
}

interface NoteDraftMap {
  [rowId: string]: string;
}

function normalizePhone(value?: string) {
  return (value ?? "").replace(/\D/g, "");
}

function normalizeText(value?: string) {
  return (value ?? "").trim().toLocaleLowerCase("tr-TR");
}

function findCustomerAccount(
  customerId: number,
  users: ReturnType<typeof useUsers>["users"]
) {
  return (
    users.find(
      (user) => user.rol === "Müşteri" && user.customerId === customerId
    ) ?? null
  );
}

function findAnimal(animals: AnimalItem[], animalId?: number) {
  if (!animalId) return null;
  return animals.find((animal) => animal.id === animalId) ?? null;
}

function findCustomer(
  customers: CustomerItem[],
  assignment: DeliveryAssignment,
  animal?: AnimalItem | null
) {
  if (animal) {
    const share = animal.hisseler.find((item) => item.id === assignment.shareId);

    if (share) {
      if (typeof share.musteriId === "number") {
        const byId = customers.find((customer) => customer.id === share.musteriId);
        if (byId) return byId;
      }

      const byPhone = customers.find(
        (customer) =>
          normalizePhone(customer.telefon) &&
          normalizePhone(customer.telefon) === normalizePhone(share.telefon)
      );
      if (byPhone) return byPhone;

      const byName = customers.find(
        (customer) =>
          normalizeText(customer.adSoyad) === normalizeText(share.musteriAdi)
      );
      if (byName) return byName;
    }
  }

  return customers.find((customer) => customer.id === assignment.customerId) ?? null;
}

function statusBadge(status: DeliveryAssignment["status"]) {
  if (status === "Teslim Edildi") {
    return "bg-farm-green-pale text-farm-green border-farm-green/20";
  }
  if (status === "Taşıma Halinde") {
    return "bg-farm-gold-pale text-farm-gold border-farm-gold/20";
  }
  return "bg-farm-earth-pale text-farm-earth border-farm-earth/20";
}

function statusButtonClass(
  currentStatus: DeliveryAssignment["status"],
  targetStatus: DeliveryAssignment["status"]
) {
  const isActive = currentStatus === targetStatus;

  if (targetStatus === "Hazır") {
    return isActive
      ? "bg-farm-earth text-white border-farm-earth"
      : "bg-white text-farm-earth border-farm-earth/20 hover:border-farm-earth/50";
  }

  if (targetStatus === "Taşıma Halinde") {
    return isActive
      ? "bg-farm-gold text-white border-farm-gold"
      : "bg-white text-farm-gold border-farm-gold/20 hover:border-farm-gold/50";
  }

  return isActive
    ? "bg-farm-green text-white border-farm-green"
    : "bg-white text-farm-green border-farm-green/20 hover:border-farm-green/50";
}

function formatDateTime(value?: string) {
  if (!value) return "—";

  try {
    return new Date(value).toLocaleString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
}

export default function TasiyiciTasimaPage() {
  const navigate = useNavigate();
  const { animals } = useAnimals();
  const { customers } = useCustomers();
  const { users } = useUsers();

  const [ready, setReady] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [version, setVersion] = useState(0);
  const [globalLiveLocation, setGlobalLiveLocation] = useState("");
  const [noteDrafts, setNoteDrafts] = useState<NoteDraftMap>({});

  useEffect(() => {
    let active = true;

    async function boot() {
      const sessionUser = readSessionUser();

      if (!sessionUser) {
        navigate("/giris", { replace: true });
        return;
      }

      setCurrentUserId(sessionUser.id);

      const restored = await restoreSessionFromApi();

      if (!active) return;

      if (!restored) {
        navigate("/giris", { replace: true });
        return;
      }

      if (mustForcePasswordChange(restored)) {
        navigate("/sifre-degistir", { replace: true });
        return;
      }

      if (restored.rol !== "Taşıyıcı") {
        navigate(getDefaultPathForRole(restored.rol), { replace: true });
        return;
      }

      setCurrentUserId(restored.id);
      setReady(true);
    }

    void boot();

    return () => {
      active = false;
    };
  }, [navigate]);

  const currentUser = useMemo(
    () => users.find((user) => user.id === currentUserId) ?? null,
    [users, currentUserId]
  );

  useEffect(() => {
    if (!ready || !currentUser) return;

    if (currentUser.durum !== "Aktif") {
      clearSessionUser();
      navigate("/giris", { replace: true });
      return;
    }

    if (mustForcePasswordChange(currentUser)) {
      navigate("/sifre-degistir", { replace: true });
      return;
    }

    if (currentUser.rol !== "Taşıyıcı") {
      navigate(getDefaultPathForRole(currentUser.rol), { replace: true });
    }
  }, [currentUser, navigate, ready]);

  useEffect(() => {
    void rehydrateDeliveryAssignments();

    return subscribeDeliveryAssignments(() => {
      setVersion((value) => value + 1);
    });
  }, []);

  const rows = useMemo<CarrierRow[]>(() => {
    if (!currentUserId) return [];

    const assignments = getDeliveryAssignments().filter(
      (assignment) => assignment.carrierUserId === currentUserId
    );

    const result: CarrierRow[] = [];

    for (const assignment of assignments) {
      const animal = findAnimal(animals, assignment.animalId);
      if (!animal) continue;

      const share = animal.hisseler.find((item) => item.id === assignment.shareId);
      const customer = findCustomer(customers, assignment, animal);

      result.push({
        id: String(assignment.id ?? assignment.shareId),
        animalId: animal.id,
        shareId: assignment.shareId,
        kupeNo: getDisplayKupeNo(animal),
        siparisNo: getDisplaySiparisNo(animal),
        tur: animal.tur,
        customerId: customer?.id ?? assignment.customerId,
        customerName:
          customer?.adSoyad ||
          share?.musteriAdi ||
          assignment.customerName ||
          "Müşteri bulunamadı",
        customerPhone: customer?.telefon || share?.telefon || "",
        address: assignment.address ?? "",
        shareNo: assignment.shareNo ?? share?.hisseNo ?? "",
        hisseSayisi: share?.hisseSayisi ?? 1,
        status: assignment.status,
        liveLocationUrl: assignment.liveLocationUrl,
        startedAt: assignment.startedAt,
        deliveredAt: assignment.deliveredAt,
        note: assignment.note,
        delivery: assignment,
      });
    }

    return result.sort((a, b) => {
      const order = {
        Hazır: 0,
        "Taşıma Halinde": 1,
        "Teslim Edildi": 2,
      } as const;

      return (
        order[a.status] - order[b.status] ||
        a.customerName.localeCompare(b.customerName, "tr") ||
        a.kupeNo.localeCompare(b.kupeNo, "tr") ||
        a.shareNo.localeCompare(b.shareNo, "tr")
      );
    });
  }, [animals, customers, currentUserId, version]);

  useEffect(() => {
    const firstActiveLink =
      rows.find(
        (row) => row.status !== "Teslim Edildi" && row.liveLocationUrl
      )?.liveLocationUrl ||
      rows.find((row) => row.liveLocationUrl)?.liveLocationUrl ||
      "";

    setGlobalLiveLocation(firstActiveLink);

    setNoteDrafts((prev) => {
      const next: NoteDraftMap = {};
      for (const row of rows) {
        next[row.id] = prev[row.id] ?? row.note ?? "";
      }
      return next;
    });
  }, [rows]);

  const counts = useMemo(
    () => ({
      hazir: rows.filter((row) => row.status === "Hazır").length,
      yolda: rows.filter((row) => row.status === "Taşıma Halinde").length,
      teslim: rows.filter((row) => row.status === "Teslim Edildi").length,
    }),
    [rows]
  );

  function saveGlobalLiveLocation() {
    if (!currentUserId) return;
    void updateCarrierLiveLocation(currentUserId, globalLiveLocation.trim());
  }

  function setStatus(row: CarrierRow, status: DeliveryAssignment["status"]) {
    if (row.status === status) return;

    const now = new Date().toISOString();

    const nextDelivery: DeliveryAssignment = {
      ...row.delivery,
      id: row.delivery.id ?? row.id,
      animalId: row.animalId,
      shareId: row.shareId,
      customerId: row.customerId,
      customerName: row.customerName,
      address: row.address,
      carrierUserId: row.delivery.carrierUserId,
      carrierId: row.delivery.carrierId,
      carrierName: row.delivery.carrierName,
      carrierPhone: row.delivery.carrierPhone,
      shareNo: row.shareNo,
      note: noteDrafts[row.id] ?? row.note ?? "",
      liveLocationUrl: row.liveLocationUrl ?? globalLiveLocation.trim(),
      status,
      startedAt:
        status === "Hazır"
          ? undefined
          : status === "Taşıma Halinde"
          ? row.startedAt ?? now
          : row.startedAt ?? now,
      deliveredAt: status === "Teslim Edildi" ? now : undefined,
    };

    void upsertDeliveryAssignment(nextDelivery);

    const customerAccount =
      row.customerId == null ? null : findCustomerAccount(row.customerId, users);

    if (status === "Taşıma Halinde" && row.customerPhone) {
      void sendSmsByTemplate({
        templateKey: "deliveryStarted",
        channel: "operational",
        eventType: "delivery_started",
        phone: row.customerPhone,
        customerId: row.customerId,
        userOptIn: customerAccount?.smsOperationalOptIn,
        variables: {
          customerName: row.customerName,
          carrierName: currentUser?.adSoyad || "Taşıyıcı",
          carrierPhone: currentUser?.telefon || "",
          address: row.address,
          kupeNo: row.kupeNo,
          shareNo: row.shareNo,
        },
      });
    }

    if (status === "Teslim Edildi" && row.customerPhone) {
      void sendSmsByTemplate({
        templateKey: "deliveryCompleted",
        channel: "operational",
        eventType: "delivery_completed",
        phone: row.customerPhone,
        customerId: row.customerId,
        userOptIn: customerAccount?.smsOperationalOptIn,
        variables: {
          customerName: row.customerName,
          carrierName: currentUser?.adSoyad || "Taşıyıcı",
          carrierPhone: currentUser?.telefon || "",
          address: row.address,
          kupeNo: row.kupeNo,
          shareNo: row.shareNo,
        },
      });
    }
  }

  function saveNote(row: CarrierRow) {
    void upsertDeliveryAssignment({
      ...row.delivery,
      id: row.delivery.id ?? row.id,
      animalId: row.animalId,
      shareId: row.shareId,
      customerId: row.customerId,
      customerName: row.customerName,
      address: row.address,
      carrierUserId: row.delivery.carrierUserId,
      carrierId: row.delivery.carrierId,
      carrierName: row.delivery.carrierName,
      carrierPhone: row.delivery.carrierPhone,
      shareNo: row.shareNo,
      note: noteDrafts[row.id] ?? "",
      liveLocationUrl: row.liveLocationUrl ?? globalLiveLocation.trim(),
      status: row.status,
      startedAt: row.startedAt,
      deliveredAt: row.deliveredAt,
    });
  }

  if (!ready || !currentUser) return null;

  return (
    <div className="min-h-screen bg-farm-cream font-body">
      <div className="max-w-[1650px] mx-auto px-3 sm:px-5 py-4 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-heading font-bold text-base text-farm-brown">
              Taşıyıcı Ekranı
            </h1>
            <p className="text-xs text-farm-muted">
              Sana atanmış teslimatlar burada görünür. Bu ekran sadece teslimat operasyonu içindir.
            </p>
          </div>

          <button
            onClick={() => navigate("/sifre-degistir")}
            className="h-9 px-3 rounded-full border border-farm-border bg-farm-warm text-farm-brown text-xs font-semibold hover:border-farm-earth/40"
          >
            Şifre Değiştir
          </button>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div className="bg-farm-warm border border-farm-border rounded-farm px-3 sm:px-4 py-3">
            <p className="text-[11px] text-farm-muted">Hazır</p>
            <p className="text-base sm:text-lg font-bold text-farm-brown mt-1">
              {counts.hazir}
            </p>
          </div>
          <div className="bg-farm-warm border border-farm-border rounded-farm px-3 sm:px-4 py-3">
            <p className="text-[11px] text-farm-muted">Taşıma Halinde</p>
            <p className="text-base sm:text-lg font-bold text-farm-gold mt-1">
              {counts.yolda}
            </p>
          </div>
          <div className="bg-farm-warm border border-farm-border rounded-farm px-3 sm:px-4 py-3">
            <p className="text-[11px] text-farm-muted">Teslim Edildi</p>
            <p className="text-base sm:text-lg font-bold text-farm-green mt-1">
              {counts.teslim}
            </p>
          </div>
        </div>

        <div className="bg-farm-warm border border-farm-border rounded-farm p-3">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
            <div>
              <p className="text-sm font-semibold text-farm-brown">
                Canlı Konum Linki
              </p>
              <p className="text-[11px] text-farm-muted">
                Buraya girilen link teslim edilmemiş paketlere ortak yansır.
              </p>
            </div>

            <button
              onClick={saveGlobalLiveLocation}
              className="h-9 px-4 rounded-full bg-farm-earth text-white text-xs font-semibold hover:bg-farm-brown"
            >
              Linki Kaydet
            </button>
          </div>

          <input
            value={globalLiveLocation}
            onChange={(e) => setGlobalLiveLocation(e.target.value)}
            placeholder="https://maps..."
            className="w-full h-10 px-3 rounded-xl border border-farm-border bg-white text-sm outline-none"
          />

          {globalLiveLocation && (
            <a
              href={globalLiveLocation}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-farm-earth hover:text-farm-brown"
            >
              <i className="ri-map-pin-line text-sm" />
              Haritada Aç
            </a>
          )}
        </div>

        {rows.length === 0 ? (
          <div className="bg-farm-warm border border-farm-border rounded-farm py-14 text-center">
            <div className="w-12 h-12 flex items-center justify-center mx-auto text-farm-muted mb-3">
              <i className="ri-truck-line text-4xl" />
            </div>
            <p className="text-sm text-farm-muted font-medium">
              Sana atanmış teslimat bulunmuyor
            </p>
          </div>
        ) : (
          <>
            <div className="lg:hidden space-y-3">
              {rows.map((row) => (
                <div
                  key={row.id}
                  className="bg-farm-warm border border-farm-border rounded-farm p-3 space-y-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-farm-brown text-sm truncate">
                        {row.customerName}
                      </p>
                      <div className="flex items-center gap-2 flex-wrap mt-1 text-[11px] text-farm-muted">
                        <span className="font-mono text-farm-brown">
                          Küpe: {row.kupeNo}
                        </span>
                        <span>•</span>
                        <span className="font-semibold text-farm-brown">
                          Sipariş: {row.siparisNo}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap mt-1 text-[11px] text-farm-muted">
                        <span className="font-semibold text-farm-brown">
                          {row.shareNo || "Hisse"}
                        </span>
                        <span>•</span>
                        <span>x{row.hisseSayisi}</span>
                        <span>•</span>
                        <span>{row.tur}</span>
                      </div>
                    </div>

                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold whitespace-nowrap ${statusBadge(
                        row.status
                      )}`}
                    >
                      {row.status}
                    </span>
                  </div>

                  <div className="space-y-2 text-[12px]">
                    <a
                      href={row.customerPhone ? `tel:${row.customerPhone}` : undefined}
                      className="flex items-center gap-2 text-farm-earth font-semibold"
                    >
                      <i className="ri-phone-line text-sm" />
                      {row.customerPhone || "Telefon yok"}
                    </a>

                    <div className="flex items-start gap-2 text-farm-brown">
                      <i className="ri-map-pin-line text-sm mt-0.5 text-farm-muted" />
                      <p className="leading-relaxed">
                        {row.address || "Adres girilmemiş"}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-[10px] text-farm-muted">
                      <div className="bg-farm-cream rounded-lg px-2 py-2">
                        <p>Başladı</p>
                        <p className="font-medium text-farm-brown mt-0.5">
                          {formatDateTime(row.startedAt)}
                        </p>
                      </div>
                      <div className="bg-farm-cream rounded-lg px-2 py-2">
                        <p>Teslim</p>
                        <p className="font-medium text-farm-brown mt-0.5">
                          {formatDateTime(row.deliveredAt)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    {(["Hazır", "Taşıma Halinde", "Teslim Edildi"] as const).map(
                      (status) => (
                        <button
                          key={status}
                          type="button"
                          onClick={() => setStatus(row, status)}
                          className={`h-9 rounded-xl border text-[11px] font-semibold ${statusButtonClass(
                            row.status,
                            status
                          )}`}
                        >
                          {status === "Taşıma Halinde" ? "Yolda" : status}
                        </button>
                      )
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="block text-[11px] font-semibold text-farm-muted">
                      Not
                    </label>
                    <div className="flex gap-2">
                      <input
                        value={noteDrafts[row.id] ?? ""}
                        onChange={(e) =>
                          setNoteDrafts((prev) => ({
                            ...prev,
                            [row.id]: e.target.value,
                          }))
                        }
                        placeholder="Teslimat notu..."
                        className="flex-1 h-10 px-3 rounded-xl border border-farm-border bg-white text-sm outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => saveNote(row)}
                        className="px-4 rounded-xl bg-farm-earth text-white text-xs font-semibold"
                      >
                        Kaydet
                      </button>
                    </div>
                  </div>

                  {(row.liveLocationUrl || globalLiveLocation) && (
                    <a
                      href={row.liveLocationUrl || globalLiveLocation}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-farm-earth hover:text-farm-brown"
                    >
                      <i className="ri-navigation-line text-sm" />
                      Canlı Konumu Aç
                    </a>
                  )}
                </div>
              ))}
            </div>

            <div className="hidden lg:block bg-farm-warm border border-farm-border rounded-farm overflow-hidden">
              <div
                className="grid bg-farm-cream border-b border-farm-border text-[10px] font-body font-semibold text-farm-muted uppercase tracking-wide"
                style={{
                  gridTemplateColumns:
                    "150px 110px 120px 110px 180px 180px 120px 180px 150px 150px",
                }}
              >
                <div className="px-3 py-2">Müşteri</div>
                <div className="px-3 py-2">Telefon</div>
                <div className="px-3 py-2">Küpe No</div>
                <div className="px-3 py-2">Sipariş No</div>
                <div className="px-3 py-2">Hisse</div>
                <div className="px-3 py-2">Adres</div>
                <div className="px-3 py-2">Durum</div>
                <div className="px-3 py-2">Not</div>
                <div className="px-3 py-2">Zaman</div>
                <div className="px-3 py-2">İşlem</div>
              </div>

              {rows.map((row) => (
                <div
                  key={row.id}
                  className="grid items-center border-b border-farm-border last:border-0 text-xs"
                  style={{
                    gridTemplateColumns:
                      "150px 110px 120px 110px 180px 180px 120px 180px 150px 150px",
                  }}
                >
                  <div className="px-3 py-3 min-w-0">
                    <p className="font-semibold text-farm-brown truncate">
                      {row.customerName}
                    </p>
                    <p className="text-[10px] text-farm-muted mt-1">{row.tur}</p>
                  </div>

                  <div className="px-3 py-3 min-w-0">
                    {row.customerPhone ? (
                      <a
                        href={`tel:${row.customerPhone}`}
                        className="text-farm-earth font-semibold truncate"
                      >
                        {row.customerPhone}
                      </a>
                    ) : (
                      <span className="text-farm-muted">—</span>
                    )}
                  </div>

                  <div className="px-3 py-3 font-mono text-farm-brown">
                    {row.kupeNo}
                  </div>

                  <div className="px-3 py-3 text-farm-brown">
                    {row.siparisNo}
                  </div>

                  <div className="px-3 py-3 min-w-0">
                    <p className="font-semibold text-farm-brown truncate">
                      {row.shareNo || "Hisse"}
                    </p>
                    <p className="text-[10px] text-farm-muted mt-1">
                      x{row.hisseSayisi}
                    </p>
                  </div>

                  <div className="px-3 py-3 min-w-0">
                    <p className="text-farm-brown truncate">
                      {row.address || "Adres girilmemiş"}
                    </p>
                  </div>

                  <div className="px-3 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold whitespace-nowrap ${statusBadge(
                        row.status
                      )}`}
                    >
                      {row.status}
                    </span>
                  </div>

                  <div className="px-3 py-3">
                    <div className="flex gap-2">
                      <input
                        value={noteDrafts[row.id] ?? ""}
                        onChange={(e) =>
                          setNoteDrafts((prev) => ({
                            ...prev,
                            [row.id]: e.target.value,
                          }))
                        }
                        placeholder="Teslimat notu..."
                        className="flex-1 h-9 px-3 rounded-xl border border-farm-border bg-white text-xs outline-none min-w-0"
                      />
                      <button
                        type="button"
                        onClick={() => saveNote(row)}
                        className="px-3 rounded-xl bg-farm-earth text-white text-[10px] font-semibold whitespace-nowrap"
                      >
                        Kaydet
                      </button>
                    </div>
                  </div>

                  <div className="px-3 py-3 text-[10px] text-farm-muted">
                    <p>
                      Başladı:{" "}
                      <span className="font-medium text-farm-brown">
                        {formatDateTime(row.startedAt)}
                      </span>
                    </p>
                    <p className="mt-1">
                      Teslim:{" "}
                      <span className="font-medium text-farm-brown">
                        {formatDateTime(row.deliveredAt)}
                      </span>
                    </p>
                  </div>

                  <div className="px-3 py-3">
                    <div className="flex flex-col gap-1.5">
                      <div className="grid grid-cols-3 gap-1.5">
                        {(["Hazır", "Taşıma Halinde", "Teslim Edildi"] as const).map(
                          (status) => (
                            <button
                              key={status}
                              type="button"
                              onClick={() => setStatus(row, status)}
                              className={`h-8 rounded-lg border text-[10px] font-semibold ${statusButtonClass(
                                row.status,
                                status
                              )}`}
                            >
                              {status === "Taşıma Halinde" ? "Yolda" : status}
                            </button>
                          )
                        )}
                      </div>

                      {(row.liveLocationUrl || globalLiveLocation) && (
                        <a
                          href={row.liveLocationUrl || globalLiveLocation}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-[10px] font-semibold text-farm-earth hover:text-farm-brown"
                        >
                          <i className="ri-navigation-line text-xs" />
                          Canlı Konumu Aç
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}